import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { buildAppUrl } from "../config/appUrls";
import { clearSignedOutStorage, readTabOwner, rememberTabOwner } from "../utils/signOutStorage";
import { endLocalSession } from "../lib/endLocalSession";

const SessionContext = createContext(undefined);

// A failed profile read (network blip, Supabase hiccup) is retried this many
// times, this far apart, before the error is surfaced. The reads themselves
// run with postgrest's own retries off: its 1 s / 2 s / 4 s backoff inside
// each of these attempts kept an offline coach on "Checking your session..."
// for ~25 s instead of ~4 s.
const PROFILE_RETRY_DELAYS_MS = [1000, 3000];
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
// A signed-in tab re-checks that the account is still active when it comes
// back into focus (supabase-js re-emits SIGNED_IN) or refreshes its token, at
// most this often.
const ACCOUNT_RECHECK_MIN_MS = 60 * 1000;

export function SessionProvider({ children, onSessionUserChange }) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfileState] = useState(null);
  // The signed-in user's profile is being read (first load or a retry).
  const [profileLoading, setProfileLoading] = useState(false);
  // The last profile read failed and there is no earlier profile to keep
  // showing: the session is fine, the app just couldn't load the account.
  const [profileError, setProfileError] = useState(null);

  // Mirrors for the auth listener, which outlives any one render.
  const userIdRef = useRef(null); // whose profile/teams are loaded (or loading)
  const profileRef = useRef(null);
  const profileLoadRef = useRef(null); // { userId, promise } of the read in flight
  const accountCheckedAtRef = useRef(0); // when the account was last read or re-checked
  const accountCheckRef = useRef(null); // the re-check in flight

  const setUserProfile = useCallback((profile) => {
    profileRef.current = profile;
    setUserProfileState(profile);
  }, []);

  const clearSessionState = useCallback(() => {
    userIdRef.current = null;
    profileLoadRef.current = null;
    accountCheckRef.current = null;
    setUserProfile(null);
    setProfileError(null);
    setProfileLoading(false);
    setUser(null);
    setSession(null);
  }, [setUserProfile]);

  const fetchUserProfile = useCallback(async (userId) => {
    if (!userId) {
      setUserProfile(null);
      return { profile: null, reason: 'no-user' };
    }
    // A failed read keeps the profile already on screen (same user): one
    // transient error used to null it out, and ProtectedRoute then sent a
    // signed-in coach to the login form.
    const keptProfile = profileRef.current?.id === userId ? profileRef.current : null;
    try {
      const { data, error } = await supabase
        .from("user_profiles")
        .select("*")
        .eq("id", userId)
        .retry(false)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error("Error fetching user profile:", error.message);
        setUserProfile(keptProfile);
        return { profile: keptProfile, reason: 'error', error };
      }

      if (!data) {
        setUserProfile(null);
        return { profile: null, reason: 'missing' };
      }

      if (data?.is_active === false) {
        setUserProfile(null);
        return { profile: null, reason: 'inactive' };
      }

      setUserProfile(data);
      setProfileError(null);
      return { profile: data, reason: null };
    } catch (error) {
      console.error("Unexpected error fetching profile:", error);
      setUserProfile(keptProfile);
      return { profile: keptProfile, reason: 'error', error };
    }
  }, [setUserProfile]);

  const handleRestrictedAccount = useCallback(async (reason = 'Account unavailable') => {
    console.warn(`Auth restriction: ${reason}. Signing out user.`);
    clearSessionState();
    onSessionUserChange?.(null, { clear: true });
    setLoading(false);
    await supabase.auth.signOut();
  }, [clearSessionState, onSessionUserChange]);

  // Reads the profile for a newly signed-in user (retrying transient errors),
  // then loads their teams. Concurrent calls for the same user share one read.
  const loadAccount = useCallback((nextUser) => {
    const inFlight = profileLoadRef.current;
    if (inFlight?.userId === nextUser.id) return inFlight.promise;

    const promise = (async () => {
      setProfileLoading(true);
      try {
        let result = await fetchUserProfile(nextUser.id);
        for (const delayMs of PROFILE_RETRY_DELAYS_MS) {
          if (result?.reason !== 'error' || userIdRef.current !== nextUser.id) break;
          await wait(delayMs);
          if (userIdRef.current !== nextUser.id) break;
          result = await fetchUserProfile(nextUser.id);
        }
        // Signed out or switched accounts while this was loading.
        if (userIdRef.current !== nextUser.id) return;

        const reason = result?.reason ?? null;
        if (!reason) {
          accountCheckedAtRef.current = Date.now();
          // Teams load on their own clock (loadingTeams); not awaited.
          onSessionUserChange?.(nextUser, { clear: false });
        } else if (['inactive', 'missing'].includes(reason)) {
          await handleRestrictedAccount(reason);
        } else if (reason === 'error') {
          // Still signed in; with no earlier profile to fall back on, say so
          // (ProtectedRoute offers a retry) instead of reading as signed out.
          setProfileError(result.error || new Error('Could not load your account.'));
        }
      } finally {
        if (profileLoadRef.current?.promise === promise) profileLoadRef.current = null;
        if (userIdRef.current === nextUser.id || userIdRef.current == null) setProfileLoading(false);
      }
    })();
    profileLoadRef.current = { userId: nextUser.id, promise };
    return promise;
  }, [fetchUserProfile, handleRestrictedAccount, onSessionUserChange]);

  // Is the loaded account still allowed in? The boot read is the only full
  // profile read for a session (76728443), so without this an account an admin
  // deactivated stayed signed in through every tab switch until a reload. Reads
  // only is_active, leaves the profile and teams alone, and treats a failed
  // read as "nothing new": a blip must never sign a coach out.
  const recheckAccount = useCallback((userId) => {
    if (accountCheckRef.current) return accountCheckRef.current;
    if (Date.now() - accountCheckedAtRef.current < ACCOUNT_RECHECK_MIN_MS) return undefined;
    accountCheckedAtRef.current = Date.now();
    const promise = (async () => {
      try {
        const { data, error } = await supabase
          .from("user_profiles")
          .select("id,is_active")
          .eq("id", userId)
          .retry(false)
          .maybeSingle();
        if (userIdRef.current !== userId) return;
        if (error) {
          console.warn("Account re-check failed; keeping the session:", error.message);
          return;
        }
        if (!data) await handleRestrictedAccount('missing');
        else if (data.is_active === false) await handleRestrictedAccount('inactive');
      } catch (error) {
        console.warn("Account re-check failed; keeping the session:", error?.message || error);
      } finally {
        if (accountCheckRef.current === promise) accountCheckRef.current = null;
      }
    })();
    accountCheckRef.current = promise;
    return promise;
  }, [handleRestrictedAccount]);

  // One path for every session report (the initial read and each auth event).
  const adoptSession = useCallback(async (event, nextSession) => {
    const nextUser = nextSession?.user ?? null;
    setSession(nextSession);

    if (!nextUser) {
      const wasSignedIn = userIdRef.current != null;
      userIdRef.current = null;
      profileLoadRef.current = null;
      accountCheckRef.current = null;
      setUser(null);
      setUserProfile(null);
      setProfileError(null);
      setProfileLoading(false);
      // The tab's delivery address/coords and the active-team pointer belong
      // to the coach who just left; the next person at this browser must not
      // inherit them.
      if (event === 'SIGNED_OUT' || wasSignedIn) clearSignedOutStorage();
      onSessionUserChange?.(null, { clear: true });
      return;
    }

    if (nextUser.id === userIdRef.current) {
      // Same coach. Tab focus re-emits SIGNED_IN, token refreshes emit
      // TOKEN_REFRESHED, and boot reports the session twice (getSession +
      // INITIAL_SESSION): only the token changed. Keep the profile, the
      // teams and the user object as they are — refetching them on every
      // tab switch flickered the team loader and remounted pages.
      if (event === 'USER_UPDATED') setUser(nextUser);
      if (profileLoadRef.current?.userId === nextUser.id) return profileLoadRef.current.promise;
      // A profile that failed to load gets another try on the next event.
      if (!profileRef.current) return loadAccount(nextUser);
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') return recheckAccount(nextUser.id);
      return undefined;
    }

    // A different coach than the one this tab was holding: one signed in
    // over the other (signInWithPassword swaps the session with no
    // SIGNED_OUT in between), or the swap happened in another tab and this
    // one reloaded. The previous coach's address, caches and share-link
    // identities must not carry over, exactly as on sign-out.
    const previousUserId = userIdRef.current ?? readTabOwner();
    if (previousUserId && previousUserId !== nextUser.id) clearSignedOutStorage();
    rememberTabOwner(nextUser.id);

    userIdRef.current = nextUser.id;
    profileLoadRef.current = null;
    setUser(nextUser);
    setProfileError(null);
    if (profileRef.current && profileRef.current.id !== nextUser.id) setUserProfile(null);
    return loadAccount(nextUser);
  }, [loadAccount, recheckAccount, onSessionUserChange, setUserProfile]);

  useEffect(() => {
    const getInitialSession = async () => {
      try {
        const { data: { session: nextSession } } = await supabase.auth.getSession();
        await adoptSession('INITIAL_SESSION', nextSession);
      } catch (error) {
        console.error("Error getting initial session:", error?.message);
      } finally {
        setLoading(false);
      }
    };

    getInitialSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, nextSession) => {
      // Not awaited: supabase-js holds its auth lock while listeners run.
      adoptSession(event, nextSession);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [adoptSession]);

  // The error stays set while the retry runs, so the "couldn't load your
  // account" card stays up (with "Trying again…") instead of flipping back to
  // the bare loading page. A good read clears it; a failed one replaces it.
  const retryProfile = useCallback(async () => {
    const currentUser = (await supabase.auth.getSession())?.data?.session?.user ?? null;
    if (!currentUser || currentUser.id !== userIdRef.current) return;
    await loadAccount(currentUser);
  }, [loadAccount]);

  const signUp = useCallback(async (email, password, userData = {}) => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData,
          emailRedirectTo: buildAppUrl('/login-registration'),
        },
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error("Sign up error:", error?.message);
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  }, []);

  const signIn = useCallback(async (email, password) => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('id,is_active')
        .eq('id', data?.user?.id)
        .maybeSingle();

      if (profileError && profileError.code !== 'PGRST116') throw profileError;

      if (!profileData) {
        await handleRestrictedAccount('missing profile after sign-in');
        throw new Error('This account has been removed. Please contact support for assistance: support@mealops.ai');
      }

      if (profileData?.is_active === false) {
        await handleRestrictedAccount('inactive profile after sign-in');
        throw new Error('This account has been deactivated. Please contact support for assistance: support@mealops.ai');
      }

      return { data, error: null };
    } catch (error) {
      console.error("Sign in error:", error?.message);
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  }, [handleRestrictedAccount]);

  // Local scope: signing out here ends this browser's session only. The
  // default (global) scope revokes every session the user holds, so a sign-out
  // on a phone silently killed the coach's laptop tab, which then failed every
  // API call while still rendering as signed in.
  //
  // A sign-out that can't reach Supabase still ends the session here (see
  // endLocalSession): the coach is told they are signed out, so they must be.
  const signOut = useCallback(async () => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      if (error) throw error;
    } catch (error) {
      console.warn("Sign out could not reach the server; ending the session on this browser:", error?.message);
      await endLocalSession();
      await adoptSession('SIGNED_OUT', null);
    } finally {
      setLoading(false);
    }
    return { error: null };
  }, [adoptSession]);

  const refreshSession = useCallback(async () => {
    try {
      const { data: { session: nextSession }, error } = await supabase.auth.refreshSession();
      if (error) {
        console.error("Session refresh error:", error?.message);
        const { error: signOutError } = await supabase.auth.signOut({ scope: 'local' });
        if (signOutError) await endLocalSession();
        return { session: null, error };
      }

      setSession(nextSession);
      setUser(nextSession?.user ?? null);
      return { session: nextSession, error: null };
    } catch (error) {
      console.error("Unexpected refresh error:", error);
      return { session: null, error };
    }
  }, []);

  const getUserProfile = useCallback(async () => {
    try {
      if (!session?.user?.id) {
        return { data: null, error: new Error("No active session") };
      }
      const { profile, reason, error } = await fetchUserProfile(session.user.id);
      if (error) throw error;
      if (reason) return { data: null, error: new Error(reason) };
      return { data: profile, error: null };
    } catch (error) {
      console.error("Get user profile error:", error?.message);
      return { data: null, error };
    }
  }, [fetchUserProfile, session?.user?.id]);

  const value = useMemo(() => ({
    user,
    session,
    loading,
    userProfile,
    profileLoading,
    profileError,
    retryProfile,
    signUp,
    signIn,
    signOut,
    getUserProfile,
    refreshSession,
    fetchUserProfile,
    handleRestrictedAccount,
  }), [
    user,
    session,
    loading,
    userProfile,
    profileLoading,
    profileError,
    retryProfile,
    signUp,
    signIn,
    signOut,
    getUserProfile,
    refreshSession,
    fetchUserProfile,
    handleRestrictedAccount,
  ]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export const useSession = () => {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error("useSession must be used within a SessionProvider");
  }
  return context;
};
